import { PaymentPane, sortByDateKey } from '@billy/design-system'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useConnectBankBanner } from '@features/bankAccounts/hooks/useConnectBankBanner'
import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { InvoiceStatus as ExternalInvoiceStatus } from '@features/invoices/enums/invoiceStatus'
import { useFetchInvoice } from '@features/invoices/hooks/useFetchInvoice'
import { getInvoiceDisplayStatus } from '@features/invoices/utils/getInvoiceDisplayStatus'
import { getInvoiceQueryKey } from '@features/invoices/utils/getInvoiceQueryKey'
import { isDocumentTypeCreditNote } from '@features/invoices/utils/isDocumentTypeCreditNote'
import { mapBillyExternalInvoiceToInvoice } from '@features/invoices/utils/mapBillyExternalInvoiceToInvoice'
import { InvoiceStatus } from '@features/invoicesLegacy/enums/invoiceStatus'
import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { useCardPayments } from '@features/transactions/hooks/useCardPayments'

import { queryClient } from '../../../../../../config/queryClient'
import { usePolling } from '../../../../../../hooks/usePolling'
import { getInvoicePaymentPaneData } from '../../../../../invoices/elements/InvoicePaymentPane/utils/getInvoicePaymentPaneData'
import { useInvoice } from '../../contexts/invoiceContext'
import { transformExternalInvoiceModifierReference } from '../../utils/transformExternalInvoiceModifierReference'
import { ExternalInvoicePaymentPaneItem } from './elements/ExternalInvoicePaymentPaneItem'

export const ExternalInvoicePaymentPane = () => {
  const { invoice, billyApiExternalInvoice, billyApiExternalInvoiceLoading } = useInvoice()
  const { invoice: referredCreditNote, isLoading: isReferredCreditNoteLoading } = useFetchInvoice(
    InvoiceDocumentType.CreditNote,
    invoice?.referredCreditnoteId,
  )
  const { t } = useTranslation()
  const connectBankBanner = useConnectBankBanner()
  const { isCardPayment, isLoading: isCardPaymentsLoading } = useCardPayments(
    billyApiExternalInvoice?.balanceModifiers.map((modifier) =>
      transformExternalInvoiceModifierReference(invoice, modifier.modifierReference),
    ),
  )
  const isLoading =
    billyApiExternalInvoiceLoading ||
    (!!invoice?.referredCreditnoteId && isReferredCreditNoteLoading) ||
    isCardPaymentsLoading

  const hasCreditNote = !!invoice?.referredCreditnoteId
  const isCreditNote = isDocumentTypeCreditNote(invoice?.documentType)
  // Zervant sets status to Paid only when the invoice is fully covered. A partial credit note leaves a different status.
  const isSettledByCreditNote = hasCreditNote && invoice?.status?.status === ExternalInvoiceStatus.Paid

  const displayStatus = useMemo(
    () => (invoice ? getInvoiceDisplayStatus(invoice, referredCreditNote?.summary.foreignGrossTotal) : undefined),
    [invoice, referredCreditNote?.summary.foreignGrossTotal],
  )

  // The credit note document itself is "Paid" once Zervant considers it fully applied to the invoice it cancels.
  const isCreditNoteFullyApplied = isCreditNote && displayStatus === ExternalInvoiceStatus.Paid

  // Poll until Zervant status and Billy balance modifiers agree (e.g. after a payment
  // recorded outside of Billy). Skip when settled by credit note — Billy will never add
  // a balance modifier for that settlement, so polling would loop forever.
  const shouldPoll =
    !hasCreditNote &&
    !isCreditNote &&
    invoice?.status?.status === ExternalInvoiceStatus.Paid &&
    billyApiExternalInvoice?.balanceModifiers?.length === 0

  usePolling({
    onPoll: () => queryClient.invalidateQueries(getInvoiceQueryKey(invoice?.id.toString(), invoice?.documentType)),
    interval: 3000,
    isEnabled: shouldPoll,
  })

  const billyInvoice = useMemo(() => {
    if (!billyApiExternalInvoice) {
      return undefined
    }

    const mappedInvoice = mapBillyExternalInvoiceToInvoice(billyApiExternalInvoice)
    // Zervant is the authoritative source for credit-note settlements: Billy never records a
    // balance modifier for them, so the settled invoice's and the credit note's Billy balance
    // never reach 0 on their own.
    if (isSettledByCreditNote || isCreditNoteFullyApplied) {
      return { ...mappedInvoice, balance: 0, isPaid: true }
    }

    return mappedInvoice
  }, [billyApiExternalInvoice, isSettledByCreditNote, isCreditNoteFullyApplied])

  const externalInvoiceDataSorted = useMemo(() => {
    return billyInvoice
      ? ({
          ...billyInvoice,
          balanceModifiers: sortByDateKey(billyInvoice.balanceModifiers || [], 'entryDate'),
        } as Invoice)
      : undefined
  }, [billyInvoice])

  const invoiceStatus = useMemo(() => {
    if (displayStatus === ExternalInvoiceStatus.Credited) {
      return InvoiceStatus.Credited
    }

    if (displayStatus === ExternalInvoiceStatus.PartiallyCredited) {
      return InvoiceStatus.PartiallyCredited
    }

    // A credit note doesn't have a due date of its own, so the legacy balance/due-date
    // fallback below can misclassify a settled credit note as Overdue. Trust the backend status
    // here instead, same as the invoice list and status ribbon do.
    if (isCreditNoteFullyApplied) {
      return InvoiceStatus.Paid
    }

    return undefined
  }, [displayStatus, isCreditNoteFullyApplied])

  const invoicePaymentPaneData = useMemo(() => {
    return getInvoicePaymentPaneData(t, externalInvoiceDataSorted, isLoading, invoiceStatus, connectBankBanner)
  }, [t, externalInvoiceDataSorted, isLoading, invoiceStatus, connectBankBanner])

  const balanceModifiers = useMemo(
    () =>
      externalInvoiceDataSorted?.balanceModifiers.map((modifier) => ({
        ...modifier,
        modifierReference: transformExternalInvoiceModifierReference(invoice, modifier.modifierReference),
      })) ?? [],
    [externalInvoiceDataSorted?.balanceModifiers, invoice],
  )
  return (
    <PaymentPane {...invoicePaymentPaneData}>
      {balanceModifiers.map(({ id, modifierReference, ...rest }) => (
        <ExternalInvoicePaymentPaneItem
          id={id}
          invoiceType={externalInvoiceDataSorted?.type ?? null}
          isCardPayment={isCardPayment(modifierReference)}
          key={id}
          modifierReference={modifierReference}
          {...rest}
        />
      ))}
    </PaymentPane>
  )
}
